import * as React from 'react';

import { Activity, ActivityFocus, ActivityFocusEditor } from './interfaces';
import { ACTIVITY_FOCUS_FILTERS, AVERAGE_FONT_WIDTH, OTHER_FOCUS_GROUP_NAME, focusGroupOrder, } from './shared';

import { requestPost, } from '../../../../../../modules/request/index';
import { Input, Snackbar, SortableList, Tooltip, defaultSnackbarTimeout, DragHandle, } from '../../../../../Shared/index';
import useSnackbarMonitor from '../../../../../Shared/hooks/useSnackbarMonitor';

const smallWhiteCheckSrc = `${window.postbuildEnv.CDN_URL}/images/shared/check-small-white.svg`
const reorderSrc = `${window.postbuildEnv.CDN_URL}/images/icons/reorder.svg`

interface StaffActivityFocusFilterRowProps {
  selectedActivityFocusId: number,
  activityFocus: ActivityFocus,
  handleActivityFocusSelect: (activityFocusId: number) => void,
  activityCount: number,
  handleRemoveActivityFocus: (id: number) => void,
  handleActivityFocusNameChange: (id: number, name: string) => void,
  timesSubmitted: number
}

interface StaffActivityFocusFiltersProps {
  filterActivities: (ignoredKey?: string) => Activity[]
  activityFocusEditor: ActivityFocusEditor
}

const activityFocusTooltipText = "Each activity is assigned one activity focus. Activity foci are the \"Activity Focus\" attribute used to filter and order activities, shown under the activity's title in the custom activity pack page. Changing the order of activity foci on this page changes the default order in which activities are displayed to teachers."

const StaffActivityFocusFilterRow = React.forwardRef<HTMLElement, StaffActivityFocusFilterRowProps & React.HTMLAttributes<HTMLElement>>(({
  selectedActivityFocusId,
  activityFocus,
  handleActivityFocusSelect,
  activityCount,
  handleRemoveActivityFocus,
  handleActivityFocusNameChange,
  timesSubmitted,
  ...sectionProps
}, ref) => {
  const originalNameRef = React.useRef(activityFocus.name)
  const [inEditMode, setInEditMode] = React.useState(false)

  React.useEffect(() => {
    setInEditMode(false)
  }, [timesSubmitted])

  function checkIndividualFilter() {
    handleActivityFocusSelect(activityFocus.id)
  }

  function uncheckIndividualFilter() {
    handleActivityFocusSelect(null)
  }

  // Capture the name at edit-start so Cancel reverts to whatever was current
  // then — not whatever was current when this row first mounted.
  function startEditing() {
    originalNameRef.current = activityFocus.name
    setInEditMode(true)
  }

  function stopEditing() {
    handleActivityFocusNameChange(activityFocus.id, originalNameRef.current)
    setInEditMode(false)
  }

  function removeActivityFocus() {
    handleRemoveActivityFocus(activityFocus.id)
  }

  function onActivityFocusNameChange(e) {
    handleActivityFocusNameChange(activityFocus.id, e.target.value)
  }

  let checkbox = <button aria-label={`Check ${activityFocus.name}`} className="focus-on-light quill-checkbox unselected" onClick={checkIndividualFilter} type="button" />

  if (selectedActivityFocusId === activityFocus.id) {
    checkbox = (<button aria-label={`Uncheck ${activityFocus.name}`} className="focus-on-light quill-checkbox selected" onClick={uncheckIndividualFilter} type="button">
      <img alt="Checked checkbox" src={smallWhiteCheckSrc} />
    </button>)
  }

  const activityFocusNameElement = activityFocus.name.length * AVERAGE_FONT_WIDTH >= 160 ? <Tooltip tooltipText={activityFocus.name} tooltipTriggerText={activityFocus.name} tooltipTriggerTextClass="tooltip-trigger-text" /> : <span>{activityFocus.name}</span>

  if (inEditMode) {
    return (
      <section {...sectionProps} id={String(activityFocus.id)} ref={ref}>
        <button aria-label="Remove activity focus" className="interactive-wrapper" onClick={removeActivityFocus} type="button"><i className="fas fa-minus-circle" /></button>
        <div className="individual-row filter-row activity-focus-row">
          <div>
            {checkbox}
            <Input
              handleChange={onActivityFocusNameChange}
              id="activity-focus-name-edit"
              value={activityFocus.name}
            />
          </div>
          <button aria-label="Cancel editing" className="interactive-wrapper" onClick={stopEditing}><i className="fas fa-times" /></button>
        </div>
      </section>
    )
  }

  return (
    <section {...sectionProps} id={String(activityFocus.id)} ref={ref}>
      <DragHandle><div aria-label="Reorder activity focus" className="focus-on-light" role="button" tabIndex={0}><img alt="Reorder icon" className="reorder-icon" src={reorderSrc} /></div></DragHandle>
      <div className="individual-row filter-row activity-focus-row">
        <div>
          {checkbox}
          {activityFocusNameElement}
        </div>
        <div className="pencil-and-count-wrapper">
          <button aria-label="Edit activity focus" className="interactive-wrapper" onClick={startEditing}><i className="fas fa-pencil-alt" /></button>
          <span>({activityCount})</span>
        </div>
      </div>
    </section>
  )
})

StaffActivityFocusFilterRow.displayName = 'StaffActivityFocusFilterRow'

const StaffActivityFocusFilters = ({ activityFocusEditor, filterActivities, }: StaffActivityFocusFiltersProps) => {
  const [newFocusNamesByCategory, setNewFocusNamesByCategory] = React.useState<Record<number, string>>({})
  const [showSnackbar, setShowSnackbar] = React.useState(false)

  useSnackbarMonitor(showSnackbar, setShowSnackbar, defaultSnackbarTimeout)

  const { activityFoci, getActivityFoci, setActivityFoci, addCreatedFocus, selectedActivityFocusId, handleActivityFocusSelect, timesSubmitted, } = activityFocusEditor

  React.useEffect(() => { getActivityFoci() }, [])

  const sortCallbackForGroup = React.useCallback((groupIds: number[]) => (sortInfo) => {
    const newOrder = sortInfo.map(item => Number(item.key))
    const inGroup = new Set(groupIds)
    const reorderedById: Record<number, number> = {}
    newOrder.forEach((id, i) => { reorderedById[id] = i })
    setActivityFoci(prev => prev.map(af => {
      if (!inGroup.has(af.id) || af.order_number === reorderedById[af.id]) { return af }
      return { ...af, order_number: reorderedById[af.id] }
    }))
  }, [setActivityFoci])

  function createNewActivityFocus(categoryId: number) {
    const name = newFocusNamesByCategory[categoryId]
    if (!name?.length) { return }

    const params = { activity_focus: { name, activity_focus_category_id: categoryId }}
    requestPost('/cms/activity_foci', params,
      (data) => {
        addCreatedFocus(data.activity_focus)
        setShowSnackbar(true)
        setNewFocusNamesByCategory({ ...newFocusNamesByCategory, [categoryId]: '' })
      }
    )
  }

  function handleNewFocusNameChange(categoryId: number, name: string) {
    setNewFocusNamesByCategory({ ...newFocusNamesByCategory, [categoryId]: name })
  }

  function handleActivityFocusNameChange(id, name) {
    setActivityFoci(activityFoci.map(af => {
      if (af.id !== id || af.name === name) { return af }
      return { ...af, name }
    }))
  }

  function handleRemoveActivityFocus(id) {
    const newActivityFoci = activityFoci.filter(af => af.id !== id)
    setActivityFoci(newActivityFoci)
  }

  const filteredActivities = filterActivities(ACTIVITY_FOCUS_FILTERS)

  const focusCountById = React.useMemo(() => {
    const counts = new Map<number, number>()
    filteredActivities.forEach(act => {
      if (act.activity_focus_id == null) { return }
      counts.set(act.activity_focus_id, (counts.get(act.activity_focus_id) || 0) + 1)
    })
    return counts
  }, [filteredActivities])

  const groupedFoci = React.useMemo(() => {
    const grouped = new Map<string, ActivityFocus[]>()
    activityFoci.forEach(af => {
      const groupName = af.activity_focus_category_name ?? OTHER_FOCUS_GROUP_NAME
      if (!grouped.has(groupName)) { grouped.set(groupName, []) }
      grouped.get(groupName).push(af)
    })
    grouped.forEach((items, name) => {
      grouped.set(name, [...items].sort((a, b) => (a.order_number ?? 0) - (b.order_number ?? 0)))
    })
    return grouped
  }, [activityFoci])

  const sortedGroupNames = React.useMemo(() => Array.from(groupedFoci.keys()).sort((a, b) => {
    const oa = focusGroupOrder(a)
    const ob = focusGroupOrder(b)
    if (oa !== ob) { return oa - ob }
    return a.localeCompare(b)
  }), [groupedFoci])

  const activityFocusList = sortedGroupNames.map(groupName => {
    const groupFoci = groupedFoci.get(groupName)
    const categoryId = groupFoci[0]?.activity_focus_category_id
    const newName = newFocusNamesByCategory[categoryId] ?? ''
    const rows = groupFoci.map(af => (<StaffActivityFocusFilterRow
      activityCount={focusCountById.get(af.id) || 0}
      activityFocus={af}
      handleActivityFocusNameChange={handleActivityFocusNameChange}
      handleActivityFocusSelect={handleActivityFocusSelect}
      handleRemoveActivityFocus={handleRemoveActivityFocus}
      key={af.id}
      selectedActivityFocusId={selectedActivityFocusId}
      timesSubmitted={timesSubmitted}
    />))
    let createButtonClassName = "quill-button-archived primary contained fun"
    if (!newName.length) { createButtonClassName += ' disabled' }
    return (
      <section className="activity-focus-group" key={groupName}>
        <h3 className="activity-focus-group-name">{groupName}</h3>
        <SortableList data={rows} helperClass="sortable-filter-row" sortCallback={sortCallbackForGroup(groupFoci.map(af => af.id))} useDragHandle={true} />
        {categoryId && (
          <section className="create-activity-focus-form">
            <Input
              handleChange={(e) => handleNewFocusNameChange(categoryId, e.target.value)}
              id={`activity-focus-name-${categoryId}`}
              label="Activity focus"
              value={newName}
            />
            <button className={createButtonClassName} onClick={() => createNewActivityFocus(categoryId)}>Add</button>
          </section>
        )}
      </section>
    )
  })

  return (
    <section aria-labelledby="activityFocusTitle" className="filter-section staff-activity-focus-filter-section">
      <Snackbar text="Changes saved" visible={showSnackbar} />
      <div className="name-and-clear-wrapper">
        <h2 id="activityFocusTitle">Activity Focus
          <Tooltip
            tooltipText={activityFocusTooltipText}
            tooltipTriggerText={<i className="fal fa-info-circle" />}
          />
        </h2>
      </div>
      {activityFocusList}
    </section>
  )
}

export default StaffActivityFocusFilters
